// What the product's top bar links to, defined once.
//
// Two renderers consume this list: the web app's Svelte header
// (apps/web/src/lib/nav/TopNav.svelte) and the static header the documentation
// build writes into every page (./header.ts, used by apps/docs/src/chrome.ts).
// They are separate builds — one is a server-rendered SvelteKit app, the other
// is a directory of files — so nothing but a shared definition keeps them
// showing the same sections. A section renamed, reordered or added here reaches
// both; a list copied into each would drift the first time only one was edited,
// and the reader who lands in the documentation from the site would find a bar
// that is not the bar they came from.

/** One entry of the top bar. */
export interface NavLink {
  /** Stable handle for the entry, independent of the label a reader sees. */
  readonly id: string;
  readonly href: string;
  readonly label: string;
}

/** The brand, which is always the way back to the site's home page. */
export const BRAND: NavLink = { id: "brand", href: "/", label: "doc.cheap" };

/** The id of the documentation entry, which each renderer treats specially. */
export const DOCS_LINK_ID = "docs";

/**
 * The public sections, in the order the bar shows them.
 *
 * The documentation's address is a parameter rather than a constant because the
 * two callers know it differently: the web app reads the address its deployment
 * publishes, and the documentation build knows the mount it is being written
 * for. Everything else is a path at the site root, so the same string works
 * from a page of either build.
 */
export function publicNavLinks(docsHref: string): readonly NavLink[] {
  return [
    { id: "passport-ocr", href: "/passport-ocr", label: "Passport OCR" },
    // The three pages written for the searches people make before they pick an
    // API. Linked from the bar rather than only from the footer and the home
    // page's body, because a crawler weighs a page by the links that reach it
    // and the bar is on every page of both builds.
    { id: "free-api", href: "/free-passport-ocr-api", label: "Free API" },
    { id: "mcp", href: "/mcp", label: "MCP" },
    { id: "compare", href: "/compare", label: "Compare" },
    { id: "tools", href: "/mrz-checker", label: "Tools" },
    { id: DOCS_LINK_ID, href: docsHref, label: "Docs" },
    { id: "pricing", href: "/pricing", label: "Pricing" },
    // The blog is in the bar for the same reason as the three pages above: the
    // footer link alone left the articles one scroll away on the home page and
    // unreachable from every page that has no footer, the documentation and
    // the cabinet included.
    { id: "blog", href: "/blog", label: "Blog" },
    { id: "status", href: "/status", label: "Status" },
  ];
}

/**
 * What the bar offers a visitor the renderer knows nothing about.
 *
 * Which side to show is a fact about the request, so a page built ahead of one
 * cannot have it: a static page always ships this pair. The web app shows the
 * same pair until it learns otherwise.
 */
export const SIGNED_OUT_LINKS: {
  readonly signIn: NavLink;
  readonly register: NavLink;
} = {
  signIn: { id: "sign-in", href: "/login", label: "Sign in" },
  register: { id: "register", href: "/register", label: "Create account" },
};
